import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Clock3, Store, UserRound } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import api from '../../lib/api';
import SubpageHeader from './SubpageHeader';

type Reservation = {
  id: number;
  store_name: string;
  staff_name: string | null;
  service_name: string | null;
  category_name?: string | null;
  service_count?: number;
  reservation_date: string;
  start_time: string;
  end_time: string;
  status: string;
};

const ReservationHistory: React.FC = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const res = await api.get('/api/customer-app/reservation/list');
        setReservations(res.data);
      } catch (err) {
        toast.error('Failed to get reservation history');
      } finally {
        setLoading(false);
      }
    };

    void fetchReservations();
  }, []);

  const historyReservations = useMemo(() => {
    const now = new Date();

    return reservations
      .filter((rsv) => {
        if (rsv.status !== 'confirmed') return true;

        const endAt = new Date(`${rsv.reservation_date}T${rsv.end_time}:00`);
        return !Number.isNaN(endAt.getTime()) && endAt < now;
      })
      .sort((a, b) => {
        const timeA = new Date(`${a.reservation_date}T${a.start_time}:00`).getTime();
        const timeB = new Date(`${b.reservation_date}T${b.start_time}:00`).getTime();
        return timeB - timeA;
      });
  }, [reservations]);

  const formatStatusLabel = (reservation: Reservation) => {
    if (reservation.status === 'confirmed') {
      return 'Awaiting update';
    }
    if (reservation.status === 'canceled') {
      return 'Canceled';
    }
    return reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1);
  };

  const getStatusTone = (reservation: Reservation) => {
    if (reservation.status === 'canceled') {
      return 'border-rose-200 bg-rose-50 text-rose-700';
    }
    if (reservation.status === 'confirmed') {
      return 'border-amber-200 bg-amber-50 text-amber-700';
    }
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  };

  const formatServiceLabel = (reservation: Reservation) => {
    const baseLabel = reservation.category_name && reservation.service_name
      ? `${reservation.category_name} – ${reservation.service_name}`
      : reservation.service_name || '-';

    return reservation.service_count && reservation.service_count > 1
      ? `${baseLabel} (+${reservation.service_count - 1} more)`
      : baseLabel;
  };

  const formatDateBlock = (date: string) => {
    const parsed = new Date(`${date}T00:00:00`);
    return {
      month: parsed.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: parsed.toLocaleDateString('en-US', { day: 'numeric' }),
      weekday: parsed.toLocaleDateString('en-US', { weekday: 'short' }),
      full: parsed.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <SubpageHeader
          title="Visit History"
          onBack={() => navigate('/reserve/mypage')}
        />
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          Loading visit history...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SubpageHeader
        title="Visit History"
        onBack={() => navigate('/reserve/mypage')}
      />

      {historyReservations.length === 0 ? (
        <div className="rounded-[30px] border border-slate-200 bg-white px-5 py-10 text-center shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
          <div className="text-base font-semibold text-slate-900">No visit history yet</div>
          <div className="mt-2 text-sm text-slate-500">
            Your past bookings will appear here.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {historyReservations.map((rsv) => {
            const dateBlock = formatDateBlock(rsv.reservation_date);

            return (
              <button
                key={rsv.id}
                type="button"
                onClick={() => navigate(`/reserve/history/${rsv.id}`)}
                className="block w-full overflow-hidden rounded-[30px] border border-slate-200 bg-white text-left shadow-[0_18px_42px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_48px_rgba(15,23,42,0.12)]"
              >
                <div className="flex gap-4 p-4">
                  <div className="flex w-[84px] shrink-0 flex-col items-center justify-center rounded-[24px] bg-[linear-gradient(180deg,#0f172a,#1e293b)] px-3 py-4 text-white shadow-sm">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/65">
                      {dateBlock.month}
                    </div>
                    <div className="mt-1 text-[30px] font-semibold leading-none">
                      {dateBlock.day}
                    </div>
                    <div className="mt-1 text-xs text-white/72">
                      {dateBlock.weekday}
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-lg font-semibold leading-snug tracking-[-0.02em] text-slate-950">
                          {formatServiceLabel(rsv)}
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          {dateBlock.full}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`shrink-0 rounded-full border px-3 py-1 text-[11px] font-semibold ${getStatusTone(rsv)}`}>
                          {formatStatusLabel(rsv)}
                        </span>
                        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-sm text-slate-700">
                        <Store className="h-4 w-4 text-slate-400" />
                        <span>{rsv.store_name}</span>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-sm text-slate-700">
                        <UserRound className="h-4 w-4 text-slate-400" />
                        <span>{rsv.staff_name || 'Anyone'}</span>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-sm text-slate-700">
                        <Clock3 className="h-4 w-4 text-slate-400" />
                        <span>{rsv.start_time} - {rsv.end_time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReservationHistory;
